import {defu} from 'defu';
import {nextTick, type ComponentPublicInstance, type Ref, computed, getCurrentInstance, onBeforeUnmount, onMounted, readonly, ref, watch} from 'vue';

interface UseScrollContainerOptions {
    /**
     * The template ref you want to target
     * @default window element
     * */
    target?: Ref<HTMLElement | ComponentPublicInstance | undefined | null>;
    /**
     * Triggers scroll listening on mount
     * @default true
     */
    immediate?: boolean;
    /**
     * Callback on scroll event
     * */
    onScroll?: (event: Event) => void;
}

/**
 * We have to check if the scroll amount is close enough to some threshold in order to
 * more accurately calculate arrivedState. This is because scrollTop/scrollLeft are non-rounded
 * numbers, while scrollHeight/scrollWidth and clientHeight/clientWidth are rounded.
 * https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
 */
const ARRIVED_STATE_THRESHOLD_PIXELS = 1;

/**
 * Observe an element scroll behaviours, returns reactive values on scroll properties
 *
 * @param options - composable options {@link UseScrollContainerOptions}
 *
 * @usages
 *
 * ```vue
 *  <template>
 *      <div ref='target' class='container scrollable'></div>
 *  </template>
 *
 * <script setup lang="ts">
 *   const target = ref<HtmlElement>();
 *
 *   const {isAtTop, isAtBottom, scrollHeight, scrollTop, startListening, stopListening, ...} = useScrollContainer({
 *      target,
 *      immediate: false,
 *      onScroll: () => {
 *         // Do stuff on scroll
 *      }
 *   })
 * </script>
 * ```
 */
export function useScrollContainer(options?: UseScrollContainerOptions) {
    const resolvedOptions = defu(options, {immediate: true});

    let resizeObserver: ResizeObserver;
    let mutationObserver: MutationObserver;

    const isAtTop = computed(() => scrollTop.value === 0);
    const isAtBottom = computed(() => scrollTop.value + containerHeight.value >= scrollHeight.value);

    /** Left side of container */
    const isAtStart = computed(() => scrollLeft.value === 0);
    /** Right side of container */
    const isAtEnd = computed(() => scrollLeft.value + containerWidth.value >= scrollWidth.value - ARRIVED_STATE_THRESHOLD_PIXELS);

    const scrollHeight = ref(0);
    const scrollWidth = ref(0);

    const containerWidth = ref(0);
    const containerHeight = ref(0);

    const scrollTop = ref(0);
    const scrollLeft = ref(0);

    const isScrollingX = computed(() => containerWidth.value < Math.abs(scrollWidth.value));
    const isScrollingY = computed(() => containerHeight.value < Math.abs(scrollHeight.value));

    const scrollContainer = ref<HTMLElement>();

    function resizeImmediateCallback() {
        if (scrollContainer.value) {
            containerWidth.value = scrollContainer.value.clientWidth;
            containerHeight.value = scrollContainer.value.clientHeight;

            scrollWidth.value = scrollContainer.value.scrollWidth;
            scrollHeight.value = scrollContainer.value.scrollHeight;
            computeScrollPosition();
        }
    }

    function handleResize() {
        nextTick(resizeImmediateCallback);
    }

    function handleScroll(event: Event) {
        window.requestAnimationFrame(() => {
            if (scrollContainer.value) {
                computeScrollPosition();
                options?.onScroll?.(event);
            }
        });
    }

    function computeScrollPosition() {
        if (scrollContainer.value) {
            scrollTop.value = scrollContainer.value.scrollTop;
            scrollLeft.value = scrollContainer.value.scrollLeft;
        }
    }

    watch([containerWidth, containerHeight, scrollWidth, scrollHeight], computeScrollPosition);

    function startListening() {
        if (resolvedOptions.target?.value) {
            if (resolvedOptions.target.value instanceof HTMLElement) {
                scrollContainer.value = resolvedOptions.target.value;
            } else if (!Array.isArray(resolvedOptions.target.value.$el)) {
                scrollContainer.value = resolvedOptions.target.value.$el as HTMLElement;
            }
        } else if (import.meta.client) {
            scrollContainer.value = document.documentElement;
        }
        if (scrollContainer.value && import.meta.client) {
            scrollContainer.value.addEventListener('scroll', handleScroll);
            mutationObserver = new MutationObserver(handleResize);
            mutationObserver.observe(scrollContainer.value, {attributes: false, childList: true, subtree: true});
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(scrollContainer.value);
            window.addEventListener('resize', handleResize);
            resizeImmediateCallback();
        } else {
            /* eslint-disable-next-line no-console */
            console.warn("[useScrollContainer] Can't find target element. Check if element exists or if there is multiple root nodes");
        }
    }

    function stopListening() {
        if (scrollContainer.value) {
            scrollContainer.value.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
        }
    }

    if (getCurrentInstance()) {
        onMounted(() => {
            if (resolvedOptions.immediate) {
                startListening();
            }
        });

        onBeforeUnmount(stopListening);
    }

    return {
        startListening,
        stopListening,
        isAtTop,
        isAtBottom,
        isAtStart,
        isAtEnd,
        scrollHeight: readonly(scrollHeight),
        scrollWidth: readonly(scrollWidth),
        containerWidth: readonly(containerWidth),
        containerHeight: readonly(containerHeight),
        scrollTop: readonly(scrollTop),
        scrollLeft: readonly(scrollLeft),
        isScrollingX,
        isScrollingY,
    };
}
